import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddLocationModal from "../../components/modal/AddLocationModal";
import AddDeviceToLocationModal from "../../components/modal/AddDeviceToLocationModal";
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal";
import {
  useGetAllLocations,
  useGetDevicesForLocation,
  useDeleteLocation,
} from "../../queries/admin";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "../../utils/string";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocationStore } from "../../store/useLocationStore";
import ImageAvatar from "../../components/ui/ImageAvatar";
import { useIsAdmin } from "../../utils/auth";

function LocationDevices({
  locationId,
  onAddDevice,
  canManage,
}: {
  locationId: string;
  onAddDevice: () => void;
  canManage: boolean;
}) {
  const navigate = useNavigate();
  const { data: devices, isLoading } = useGetDevicesForLocation(locationId);

  if (isLoading) {
    return (
      <div className="py-3 px-3 text-xs text-gray-400 dark:text-gray-500 animate-pulse">
        Loading devices...
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {devices && devices.length > 0 ? (
        devices.map((device: any) => (
          <div
            key={device.id}
            onClick={() => navigate(`/devices/${device.id}`)}
            className="group/leaf flex items-center justify-between p-2.5 bg-white/50 dark:bg-gray-800/30 border-b border-gray-200/5 dark:border-gray-700/10 hover:bg-gray-100 dark:hover:bg-gray-700/40 transition-all rounded cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <ImageAvatar imageKey={device.imageKey} name={device.name} size="sm" />
              <span className="text-xs font-medium text-gray-900 dark:text-gray-200">
                {device.name}
              </span>
              {device.status && (
                <span
                  className={classNames(
                    "inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-tight font-semibold",
                    device.status === "ACTIVATED"
                      ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                      : device.status === "REGISTERED"
                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                        : device.status === "CONNECTED"
                          ? "bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400",
                  )}
                >
                  {device.status}
                </span>
              )}
            </div>
            {canManage && (
              <div
                className="flex items-center gap-1 opacity-0 group-hover/leaf:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <button className="p-1 hover:bg-gray-200/60 dark:hover:bg-gray-600/40 text-gray-400 dark:text-gray-500 rounded transition-colors">
                  <PencilIcon className="h-3.5 w-3.5" />
                </button>
                <button className="p-1 hover:bg-red-100/60 dark:hover:bg-red-900/20 text-red-400 dark:text-red-500 rounded transition-colors">
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="py-3 px-3 text-xs text-gray-400 dark:text-gray-500 italic">
          No devices at this location
        </div>
      )}

      {/* Add Device button */}
      {canManage && (
        <button
          onClick={onAddDevice}
          className="flex items-center gap-2 p-2.5 w-full text-xs font-medium text-primary dark:text-blue-400 hover:bg-primary/5 dark:hover:bg-blue-400/5 rounded transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Add Device
        </button>
      )}
    </div>
  );
}

function LocationDeviceCount({ locationId }: { locationId: string }) {
  const { data: devices } = useGetDevicesForLocation(locationId);
  const count = devices?.length || 0;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 px-3 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
      {count} {count === 1 ? "Device" : "Devices"}
    </span>
  );
}

function LocationTreeNode({
  location,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onAddDevice,
  canManage,
}: {
  location: any;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (location: any) => void;
  onDelete: (locationId: string, locationName: string) => void;
  onAddDevice: (locationId: string, locationName: string) => void;
  canManage: boolean;
}) {
  return (
    <div className="group">
      {/* L1 Node - Location */}
      <div
        onClick={onToggle}
        className="relative flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-4 cursor-pointer hover:shadow-sm transition-shadow"
      >
        <div className="flex items-center gap-4">
          <ImageAvatar imageKey={location.imageKey} name={location.name} />
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white text-sm">
              {location.name}
            </h3>
            {(location.type || location.address) && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                {location.type || location.address}
              </p>
            )}
          </div>
        </div>

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LocationDeviceCount locationId={location.id} />
        </div>

        {canManage && (
          <div
            className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => onEdit(location)}
              className="p-1.5 hover:bg-gray-200/60 dark:hover:bg-gray-600/40 text-gray-400 dark:text-gray-500 rounded-md transition-colors"
              title="Edit"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(location.id, location.name)}
              className="p-1.5 hover:bg-red-100/60 dark:hover:bg-red-900/20 text-red-400 dark:text-red-500 rounded-md transition-colors"
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* L2 Container - Devices (Tree Branch) */}
      {isExpanded && (
        <div className="ml-10 mt-2 border-l border-gray-300/30 dark:border-gray-600/30 pl-6 relative">
          <LocationDevices
            locationId={location.id}
            onAddDevice={() => onAddDevice(location.id, location.name)}
            canManage={canManage}
          />
        </div>
      )}
    </div>
  );
}

export default function LocationPage() {
  const isAdmin = useIsAdmin();
  const { data: locations } = useGetAllLocations();
  const { mutate: deleteLocation } = useDeleteLocation();
  const queryClient = useQueryClient();
  const { setSelectedLocation } = useLocationStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalConfig, setDeleteModalConfig] = useState<{
    title: string;
    itemName: string;
    itemType: string;
    onConfirm: () => void;
  } | null>(null);

  const filteredLocations = useMemo(() => {
    if (!locations) return [];
    if (!searchTerm) return locations;
    const term = searchTerm.toLowerCase();
    return locations.filter(
      (loc: any) =>
        loc.name?.toLowerCase().includes(term) ||
        loc.address?.toLowerCase().includes(term) ||
        loc.type?.toLowerCase().includes(term),
    );
  }, [locations, searchTerm]);

  const handleDeleteLocation = (locationId: string, locationName: string) => {
    setDeleteModalConfig({
      title: "Delete Location",
      itemName: locationName,
      itemType: "location",
      onConfirm: () => {
        deleteLocation(locationId, {
          onSuccess: () => {
            toast.success("Location deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["locations", "all"] });
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.errorMessage || "Failed to delete location");
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          },
        });
      },
    });
    setShowDeleteModal(true);
  };

  const toggleExpand = (locationId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  };


  const handleEditLocation = (location: any) => {
    setEditingLocation(location);
    setShowAddLocationModal(true);
  };

  const handleAddDevice = (locationId: string, locationName: string) => {
    setSelectedLocation(locationId, locationName);
    setShowAddDeviceModal(true);
  };

  return (
    <>
      <div className="flex-1 flex flex-col gap-8">
        {/* Header */}
        <header className="flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Site Directory
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-lg">
              Manage and monitor all locations and their connected devices. Expand each site to view its device hierarchy.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                setEditingLocation(null);
                setShowAddLocationModal(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              New Location
            </button>
          )}
        </header>

        {/* Search */}
        <div className="flex items-center w-full gap-4">
          <div className="relative flex-1 max-w-[50%]">
            <input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Icon
              icon="mdi:magnify"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            />
          </div>
        </div>

        {/* Tree Structure */}
        <div className="flex-1 flex flex-col space-y-4">
          {filteredLocations && filteredLocations.length > 0 ? (
            filteredLocations.map((location: any) => (
              <LocationTreeNode
                key={location.id}
                location={location}
                isExpanded={expandedIds.has(location.id)}
                onToggle={() => toggleExpand(location.id)}
                onEdit={handleEditLocation}
                onDelete={handleDeleteLocation}
                onAddDevice={handleAddDevice}
                canManage={isAdmin}
              />
            ))
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center bg-white dark:bg-[#111B21] rounded-xl border border-gray-200/30 dark:border-gray-700/30">
              <img
                src="/sidenav/location.svg"
                alt=""
                className="mx-auto h-20 w-20 opacity-60 dark:invert"
              />
              <h3 className="mt-4 text-lg font-bold text-gray-900 dark:text-white">
                No locations
              </h3>
              <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                Get started by creating a new location.
              </p>
            </div>
          )}
        </div>
      </div>

      {showAddLocationModal && (
        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => {
            setShowAddLocationModal(false);
            setEditingLocation(null);
          }}
          editingLocation={editingLocation}
        />
      )}

      {showDeleteModal && deleteModalConfig && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteModalConfig(null);
          }}
          onConfirm={deleteModalConfig.onConfirm}
          title={deleteModalConfig.title}
          itemName={deleteModalConfig.itemName}
          itemType={deleteModalConfig.itemType}
        />
      )}

      {showAddDeviceModal && (
        <AddDeviceToLocationModal
          isOpen={showAddDeviceModal}
          onClose={() => setShowAddDeviceModal(false)}
        />
      )}
    </>
  );
}
